import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "ui/button";
import { StateNvr, createNvr, deleteCamera, revokeLicense } from "lib/actions";
import React, { useContext, useEffect, useState, useMemo } from "react";
import OTPInput from "ui/OTPInput/index";
import { AuthContext } from "context/AuthContext";
import { transFileSize } from "lib/utils";
import { showToast } from "ui/components/common";
import Modal from "react-modal";
import { ConfirmModalStyles } from "lib/constants";
import { TrashIcon } from "@heroicons/react/20/solid";
import { SideviewContext } from "context/SideviewContext";
import _ from "lodash";
import "./edit-form.scss";
import { useCustomersService } from "pages/dashboard/customers/service";
import type { WsCameraSettingReq } from "pages/dashboard/customers/service";
import moment from "moment";
import { getCurrencySymbol } from "lib/utils";
import { CameraInfo, WsResDataType } from "lib/types";
import { Alert, Select, Button as ButtonAntd } from "antd";
import { getCameraDetail } from "pages/service";
import PubSub from "pubsub-js";
export default function Form({
  orgId,
  siteId,
  objData,
}: {
  orgId: string;
  siteId: string;
  objData: CameraInfo & { upgrade: any };
}) {
  const { setOpenPanel, setViewData } = useContext(SideviewContext);

  const { sendUpdateCameraSettingRequest, upgradeEdge } = useCustomersService();
  const [revokePending, setRevokePending] = useState(false);
  const [deletingPending, setDeletingPending] = useState(false);
  const [removeConfirmModalOpen, setRemoveConfirmModalOpen] = useState(false);
  const [revokeConfirmModalOpen, setRevokeConfirmModalOpen] = useState(false);
  const [deleteRefundModalOpen, setDeleteRefundModalOpen] = useState(false);

  const [cameraSettingLoading, setCameraSettingLoading] = useState(false);

  const [cameraSetting, setCameraSetting] = useState<WsCameraSettingReq>();

  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const canRevoke =
    objData?.expiredAt &&
    Number(objData?.expiredAt) > new Date().getTime() &&
    !objData.isFreeTrial;

  useEffect(() => {
    setCameraSetting({
      id: +objData?.id,
      name: objData?.name,
      mainStream: objData?.mainStream,
      subStream: objData?.subStream,
    });
  }, [objData]);

  async function handleRemoveCamera(id, refund = false) {
    try {
      console.log("Removing camera with id = ", id);
      setDeletingPending(true);
      await deleteCamera(orgId, id);
      showToast(0, "The Camera has been removed successfully!");
      PubSub.publish("site-device-update", { siteId });
      setRemoveConfirmModalOpen(false);
      if (refund) {
        setDeleteRefundModalOpen(true);
      } else {
        setOpenPanel(false);
      }

      // navigate(0);
    } catch (error) {
      console.error(error);
    }
    setDeletingPending(false);
  }

  const used = useMemo(() => {
    if (!objData || !Array.isArray(objData.quotaUsages)) {
      return "-/-";
    }
    const strogeUsed = objData.quotaUsages.find(
      (useage) => useage.unitType === "goods_unit_type_alert_storage",
    );
    if (!strogeUsed) {
      return "-/-";
    }
    return (
      transFileSize(strogeUsed.accumuUsage) +
      " / " +
      transFileSize(strogeUsed.accumuAmount)
    );
  }, [objData]);

  const handleRevoke = async () => {
    try {
      setRevokePending(true);
      console.log("Revoke license with id = ", objData.id);
      const req = {
        targetType: "target_camera",
        targetIdList: [objData.id],
      };
      await revokeLicense(orgId, req);
      showToast(0, "The License has been terminated successfully!");
      PubSub.publish("site-device-update", { siteId });
    } catch (error) {
      console.error(error);
      showToast(1, "Failed to revoke the license. Please try again.");
    }
    setOpenPanel(false);
    setRevokePending(false);
  };
  const handleSave = async () => {
    setCameraSettingLoading(true);
    try {
      const res = await sendUpdateCameraSettingRequest([cameraSetting], orgId);
      const data = (res as WsResDataType)?.deviceUpdateCamerasSettingsReply
        ?.data;

      if (
        (data?.length > 0 &&
          !data?.at(0)?.nameRes?.error &&
          !data?.at(0)?.streamSettingsRes?.error) ||
        data?.length === 0
      ) {
        objData.name = cameraSetting.name;
        objData.mainStream = cameraSetting.mainStream;
        objData.subStream = cameraSetting.subStream;
        showToast(0, "The Camera has been updated successfully!");
        //globalMutate([updateQueryUrl, user.token, orgId]);
        setOpenPanel(false);
      } else {
        showToast(1, "Failed to update the Camera. Please try again later.");
      }
    } catch (error) {
      console.error(error);
      showToast(1, "Failed to update the Camera. Please try again later.");
    }

    setCameraSettingLoading(false);
  };

  const upgradable = () => {
    if (
      ["success", "failed", "empty"].includes(
        objData.upgrade?.upgradingState,
      ) &&
      objData.status === "online"
    ) {
      const camera_edge = objData.upgrade?.services?.find(
        (item) => item.service === "camera_edge",
      );
      if (camera_edge && camera_edge.upgradable === "need_upgrade") {
        return true;
      }
    }
    return false;
  };

  const upgradeEdgeCamera = async () => {
    setUpgradeLoading(true);
    try {
      await upgradeEdge(orgId, objData.id);
      setUpgradeLoading(false);
      setViewData((state) => {
        const _state = _.cloneDeep(state);
        _state.objData.upgrade.upgradingState = "upgrading";
        const camera_edge = _state.objData.upgrade?.services?.find(
          (item) => item.service === "camera_edge",
        );
        camera_edge.upgradingState = "upgrading";
        return _state;
      });
      const intervalId = setInterval(() => {
        // Poll edge_camera upgrade status
        getCameraDetail(orgId, objData.id, "?withUp=true").then((data) => {
          const edgeCamera = data.data;
          if (
            !["upgrading", "initial"].includes(
              edgeCamera.upgrade?.upgradingState,
            )
          ) {
            if (edgeCamera.upgrade?.upgradingState === "success")
              showToast(
                0,
                "Upgrade result:" + edgeCamera.upgrade?.upgradingState,
              );
            else
              showToast(
                1,
                "Upgrade result:" + edgeCamera.upgrade?.upgradingState,
              );
            clearInterval(intervalId);
            setViewData((state) => {
              const _state = _.cloneDeep(state);
              _state.objData.upgrade = edgeCamera.upgrade;
              return _state;
            });
          }
        });
      }, 1000);
    } catch (e) {
      setUpgradeLoading(false);
      showToast(1, "Upgrade failed");
    }
  };

  return (
    <>
      {/* <div>{JSON.stringify(objData)}</div> */}
      <div className="bg-zinc-50 p-6 mt-3 rounded-lg">
        <table className="table-auto gap-y-3 text-neutral-800 ">
          <tbody>
            <tr>
              <td className="pb-3 text-neutral-500">Model</td>
              <td className="pb-3 pl-3">{objData.model}</td>
            </tr>
            <tr>
              <td className="pb-3 text-neutral-500">Vendor</td>
              <td className="pb-3 pl-3">{objData.manufacturer}</td>
            </tr>
            <tr>
              <td className="pb-3 text-neutral-500 text-nowrap">MAC Address</td>
              <td className="pb-3 pl-3">{objData.macAddress}</td>
            </tr>
            <tr>
              <td className="pb-3 text-neutral-500 text-nowrap">IP Adress</td>
              <td className="pb-3 pl-3">{objData.ipAddress}</td>
            </tr>
            <tr>
              <td className="text-neutral-500 text-nowrap">Serial Number</td>
              <td className="pl-3 pb-3">{objData.sn}</td>
            </tr>

            <tr>
              <td className="pb-3 text-neutral-500 text-nowrap">
                Edge Version
              </td>
            </tr>
            <tr>
              <td className="pb-3 text-neutral-500 text-nowrap">
                Current Version
              </td>
              <td className="pb-3 pl-3">
                {objData.upgrade?.services?.find(
                  (item) => item.service === "camera_edge",
                )?.version || "-"}
              </td>
            </tr>
            <tr>
              <td className="pb-3 text-neutral-500 text-nowrap">
                Target Version
              </td>
              <td className="pb-3 pl-3">
                {objData.upgrade?.services?.find(
                  (item) => item.service === "camera_edge",
                )?.targetVersion || "-"}
              </td>
            </tr>
          </tbody>
        </table>
        {["upgrading", "initial"].includes(
          objData.upgrade?.services?.find(
            (item) => item.service === "camera_edge",
          )?.upgradingState,
        ) && (
          <Alert
            style={{ marginBottom: "12px" }}
            message="Upgrading, please do not turn off or restart the device."
            type="info"
            showIcon
          />
        )}
        {["failed"].includes(
          objData.upgrade?.services?.find(
            (item) => item.service === "camera_edge",
          )?.upgradingState,
        ) && (
          <Alert
            style={{ marginBottom: "12px" }}
            message="Upgrade failed, please Try Again or contact support.."
            type="error"
            showIcon
          />
        )}
        <div style={{ display: "flex", justifyContent: "end", width: "100%" }}>
          <ButtonAntd
            disabled={!upgradable()}
            className="primaryButton"
            onClick={upgradeEdgeCamera}
            loading={upgradeLoading}
          >
            Upgrade Edge
          </ButtonAntd>
        </div>
      </div>
      <div className="bg-zinc-50 p-6 mt-3 rounded-lg">
        <div className="font-semibold w-full flex justify-between mb-4">
          <span>License</span>
          <button
            disabled={!canRevoke}
            className="text-blue-500 hover:text-blue-400 disabled:text-blue-200 disabled:cursor-not-allowed"
            onClick={() => {
              setRevokeConfirmModalOpen(true);
            }}
          >
            Revoke
          </button>
        </div>
        <div className="text-neutral-500 w-5/6 flex justify-between mb-4">
          <span>Expiry Date</span>
          <span className="text-neutral-800">
            {objData.expiredAt
              ? "Exp " +
                moment(parseInt(objData.expiredAt)).format("YYYY/MM/DD")
              : "-"}
          </span>
        </div>
        <div className="text-neutral-500 w-5/6 flex justify-between mb-4">
          <span>Algorithm</span>
          <span></span>
        </div>
        <div className="text-neutral-500 w-5/6 flex justify-between">
          <span className="text-nowrap">Storage Used</span>
          <span className="text-neutral-800 text-nowrap">{used}</span>
        </div>
      </div>
      <div className="mt-4">
        <div className="mb-2 text-sm">Camera Name</div>
        <input
          id="name"
          name="name"
          type="text"
          className="block w-full rounded-2xl border border-gray-200 px-3 py-4 text-sm outline-2 placeholder:text-gray-500 focus-visible:outline-none "
          aria-describedby="name-error"
          defaultValue={objData.name}
          onChange={(event) => {
            setCameraSetting({
              ...cameraSetting,
              name: event.target.value,
            });
          }}
        />
      </div>

      <div className="mt-4  w-full flex mb-4 gap-3 text-neutral-800">
        <div className="w-1/3 select-container">
          HD
          <Select
            className="drawer-select"
            popupClassName="camera-edit-select-dropdown"
            style={{ width: "100%", borderRadius: "16px" }}
            dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
            value={cameraSetting?.mainStream?.resolution?.name}
            onChange={(value) => {
              const resolution =
                objData?.capabilities?.mainStream?.resolutions?.find(
                  (resolution) => resolution.name === value,
                );
              let newBiteRate;
              if (
                !resolution?.bitRates?.includes(
                  cameraSetting?.mainStream?.bitRate,
                )
              ) {
                newBiteRate = resolution?.defaultBitRate;
              }

              setCameraSetting({
                ...cameraSetting,
                mainStream: {
                  ...cameraSetting.mainStream,
                  bitRate: newBiteRate,
                  frameRate: resolution?.frameRates?.at(0),
                  resolution: {
                    height: resolution.height,
                    width: resolution.width,
                    name: resolution.name,
                  },
                },
              });
            }}
            options={objData?.capabilities?.mainStream?.resolutions?.map(
              (resolution) => {
                return {
                  value: resolution.name,
                  label: resolution.name,
                };
              },
            )}
          />
        </div>
        <div className="w-1/3 select-container">
          Frame Rate
          <Select
            className="drawer-select"
            popupClassName="camera-edit-select-dropdown"
            style={{ width: "100%" }}
            dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
            value={cameraSetting?.mainStream?.frameRate}
            onChange={(value) => {
              setCameraSetting({
                ...cameraSetting,
                mainStream: {
                  ...cameraSetting.mainStream,
                  frameRate: +value,
                },
              });
            }}
            options={objData?.capabilities?.mainStream?.resolutions
              ?.find(
                (r) => r.name === cameraSetting?.mainStream?.resolution?.name,
              )
              ?.frameRates.map((frameRate) => {
                return {
                  value: frameRate,
                  label: frameRate,
                };
              })}
          />
        </div>
        <div className="w-1/3 select-container">
          Bit Rate
          <Select
            className="drawer-select"
            popupClassName="camera-edit-select-dropdown"
            style={{ width: "100%" }}
            dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
            value={cameraSetting?.mainStream?.bitRate}
            onChange={(value) => {
              setCameraSetting({
                ...cameraSetting,
                mainStream: {
                  ...cameraSetting.mainStream,
                  bitRate: +value,
                },
              });
            }}
            options={objData?.capabilities?.mainStream?.resolutions
              ?.find(
                (r) => r.name === cameraSetting?.mainStream?.resolution?.name,
              )
              ?.bitRates?.map((bitRate) => {
                return {
                  value: bitRate,
                  label: bitRate,
                };
              })}
          />
        </div>
      </div>

      <div className="mt-3  w-full flex mb-4 gap-3 text-neutral-800">
        <div className="w-1/3 select-container">
          SD
          <Select
            className="drawer-select"
            popupClassName="camera-edit-select-dropdown"
            style={{ width: "100%", borderRadius: "16px" }}
            dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
            value={cameraSetting?.subStream?.resolution?.name}
            onChange={(value) => {
              const resolution =
                objData?.capabilities?.subStream?.resolutions?.find(
                  (resolution) => resolution.name === value,
                );
              let newBiteRate;
              if (
                !resolution?.bitRates?.includes(
                  cameraSetting?.subStream?.bitRate,
                )
              ) {
                newBiteRate = resolution?.defaultBitRate;
              }

              setCameraSetting({
                ...cameraSetting,
                subStream: {
                  ...cameraSetting.subStream,
                  bitRate: newBiteRate,
                  frameRate: resolution?.frameRates?.at(0),
                  resolution: {
                    height: resolution.height,
                    width: resolution.width,
                    name: resolution.name,
                  },
                },
              });
            }}
            options={objData?.capabilities?.subStream?.resolutions?.map(
              (resolution) => {
                return {
                  value: resolution.name,
                  label: resolution.name,
                };
              },
            )}
          />
        </div>
        <div className="w-1/3 select-container">
          Frame Rate
          <Select
            className="drawer-select"
            popupClassName="camera-edit-select-dropdown"
            style={{ width: "100%" }}
            dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
            value={cameraSetting?.subStream?.frameRate}
            onChange={(value) => {
              setCameraSetting({
                ...cameraSetting,
                subStream: {
                  ...cameraSetting.subStream,
                  frameRate: +value,
                },
              });
            }}
            options={objData?.capabilities?.subStream?.resolutions
              ?.find(
                (r) => r.name === cameraSetting?.subStream?.resolution?.name,
              )
              ?.frameRates.map((frameRate) => {
                return {
                  value: frameRate,
                  label: frameRate,
                };
              })}
          />
        </div>
        <div className="w-1/3 select-container">
          Bit Rate
          <Select
            className="drawer-select"
            popupClassName="camera-edit-select-dropdown"
            style={{ width: "100%" }}
            dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
            value={cameraSetting?.subStream?.bitRate}
            onChange={(value) => {
              setCameraSetting({
                ...cameraSetting,
                subStream: {
                  ...cameraSetting.subStream,
                  bitRate: +value,
                },
              });
            }}
            options={objData?.capabilities?.mainStream?.resolutions
              ?.find(
                (r) => r.name === cameraSetting?.mainStream?.resolution?.name,
              )
              ?.bitRates?.map((bitRate) => {
                return {
                  value: bitRate,
                  label: bitRate,
                };
              })}
          />
        </div>
      </div>
      <div className="mt-8 flex justify-between">
        <button
          className="cursor-pointer flex items-center rounded-2xl bg-white px-[24px] py-[14px] text-base font-semibold text-blue-500 border border-blue-500 transition-colors hover:text-blue-400 hover:border-blue-400 "
          onClick={() => {
            setOpenPanel(false);
          }}
        >
          Close
        </button>
        <div className="flex justify-end">
          <Button
            className="bg-white w-[84px] h-[54px] hover:bg-transparent cursor-pointer"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setRemoveConfirmModalOpen(true);
            }}
          >
            <span className="text-red-500 hover:text-red-400">Delete</span>
          </Button>
          <Button
            disabled={!cameraSetting?.name}
            className="bg-blue-500 w-[84px] h-[54px] rounded-2xl flex items-center justify-center text-base font-semibold text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleSave}
          >
            {cameraSettingLoading ? (
              <div
                className="ml-1 text-white  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>
      <Modal
        isOpen={removeConfirmModalOpen}
        style={ConfirmModalStyles}
        contentLabel="Example Modal"
      >
        <h1 className="text-xl font-bold">
          Are you sure you want to delete this camera?
        </h1>
        <div className="text-base mt-4">
          {canRevoke
            ? "This camera has an active license. Deleting it will void the license, remove all associated alerts, and stop any associated services. Are you sure you want to proceed?"
            : "Deleting this camera will remove all associated alerts. Are you sure you want to proceed?"}
        </div>

        <div className="flex items-center justify-end gap-4 mt-6">
          <button
            className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => {
              setRemoveConfirmModalOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            disabled={deletingPending}
            className="bg-[#F73D2F] w-[174px] h-[54px] rounded-2xl flex items-center justify-center text-base font-semibold text-white transition-colors hover:bg-[#f85c50] disabled:hover:bg-[#f4aea9] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              handleRemoveCamera(objData.id, canRevoke);
            }}
          >
            {canRevoke ? "Delete and Refund" : "Delete"}
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={revokeConfirmModalOpen}
        style={ConfirmModalStyles}
        contentLabel="Example Modal"
      >
        <h1 className="text-xl font-bold">Revoke License</h1>
        <div className="bg-zinc-50 p-6 mt-3 rounded-lg">
          <div className="font-semibold w-full mb-4">
            <span>License</span>
          </div>
          <div className="text-neutral-500 w-5/6 flex justify-between mb-4">
            <span>Expiry Date</span>
            <span className="text-neutral-800">
              {objData.expiredAt
                ? "Exp " +
                  moment(parseInt(objData.expiredAt)).format("YYYY/MM/DD")
                : "-"}
            </span>
          </div>
          <div className="text-neutral-500 w-5/6 flex justify-between mb-4">
            <span>Algorithm</span>
            <span>-</span>
          </div>
          <div className="text-neutral-500 w-5/6 flex justify-between mb-4">
            <span className="text-nowrap">Storage Used</span>
            <span className="text-neutral-800 text-nowrap">{used}</span>
          </div>
        </div>

        <div className="bg-zinc-50 p-6 mt-3 rounded-lg">
          <div className="font-semibold w-full mb-4">
            <span>Refund Details</span>
          </div>
          <div className="text-neutral-500 w-5/6 flex justify-between mb-4">
            <span>Refund Amount</span>
            <span className="text-neutral-800">
              {getCurrencySymbol(objData?.refundPrice?.currency)}
              {objData?.refundPrice?.amount / 100 || "-"}
            </span>
          </div>
          <div className="text-neutral-500 w-5/6 flex justify-between mb-4">
            <span>Refund Method</span>
            <span className="text-neutral-800 text-nowrap">Credit Card</span>
          </div>
          <div className="text-neutral-500 w-5/6 flex justify-between mb-4">
            <span className="text-nowrap">Processing Time</span>
            <span className="text-neutral-800 text-nowrap">
              5-7 business days
            </span>
          </div>
        </div>

        <div className="flex items-center justify-end mt-6 gap-4">
          <button
            className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => {
              setRevokeConfirmModalOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            disabled={revokePending}
            className="bg-blue-500 w-[104px] h-[54px] rounded-2xl flex items-center justify-center text-base font-semibold text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              setRevokeConfirmModalOpen(false);
              handleRevoke();
            }}
          >
            {revokePending ? (
              <div
                className="ml-1 text-white  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
            ) : (
              "Confirm"
            )}
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={deleteRefundModalOpen}
        style={ConfirmModalStyles}
        contentLabel="Example Modal"
      >
        <h1 className="text-xl font-bold">
          The camera has been successfully deleted.
        </h1>
        <div className="text-base mt-4">
          A refund of {getCurrencySymbol(objData?.refundPrice?.currency)}
          {objData?.refundPrice?.amount / 100 || "-"} for the unused portion of
          the license will be processed to your account within 5-7 business
          days.
        </div>

        <div className="flex items-center justify-end mt-6">
          <button
            className="bg-blue-500 w-[174px] h-[54px] rounded-2xl flex items-center justify-center text-base font-semibold text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => {
              setDeleteRefundModalOpen(false);
              setOpenPanel(false);
            }}
          >
            Got it
          </button>
        </div>
      </Modal>
    </>
  );
}
